'use client';
import React, { useState, useEffect, useCallback } from 'react';
import {
  Plus, Play, Pause, Users, MessageSquare, CheckCircle, FileText, Send, Eye, Edit2, Zap, X, Save,
  Loader2, RefreshCw, Upload, Image, List, MousePointerClick, MessageCircle, LayoutTemplate,
  Layers, ShoppingCart, Shield, Workflow, ChevronLeft, ChevronRight, Type, GripVertical, Trash2, ArrowUp, ArrowDown,
} from 'lucide-react';
import { toast } from 'sonner';
import { createClient } from '@/lib/supabase/client';
import Icon from '@/components/ui/AppIcon';
import TrackableLinkGenerator from './TrackableLinkGenerator';


type CampaignStatus = 'Active' | 'Paused' | 'Draft' | 'Completed';

interface Campaign {
  id: string;
  name: string;
  status: CampaignStatus;
  segment: string;
  enrolled: number;
  sent: number;
  delivered: number;
  opened: number;
  deliveryRate: string;
  openRate: string;
  startDate: string;
  dripDays: number;
}

interface WaTemplate {
  id: string;
  name: string;
  label: string;
  body: string;
  category: string;
  language: string;
  status: string;
  uses: number;
  content_sid?: string | null;
  twilio_approval_status?: string | null;
  submitted_at?: string | null;
  sample_values?: Record<string, string> | null;
  content_type?: string | null;
  media_url?: string | null;
  media_content_type?: string | null;
  form_payload?: Record<string, unknown> | null;
}

// ─── Content Type Definitions ───────────────────────────────────────────────

type ContentTypeKey =
  | 'twilio/text' |'twilio/media' |'twilio/list-picker' |'twilio/call-to-action' |'twilio/quick-reply' |'twilio/card' |'twilio/carousel' |'twilio/whatsapp-card' |'twilio/authentication' |'twilio/catalog' |'twilio/flows';

interface ContentTypeOption {
  key: ContentTypeKey;
  label: string;
  description: string;
  icon: React.ElementType;
  color: string;
}

const CONTENT_TYPES: ContentTypeOption[] = [
  { key: 'twilio/text', label: 'Text', description: 'Simple text message with optional variables', icon: Type, color: 'text-blue-600 bg-blue-50' },
  { key: 'twilio/media', label: 'Media', description: 'Image, video, or document with caption', icon: Image, color: 'text-purple-600 bg-purple-50' },
  { key: 'twilio/list-picker', label: 'List Picker', description: 'Interactive list with selectable items', icon: List, color: 'text-green-600 bg-green-50' },
  { key: 'twilio/call-to-action', label: 'Call to Action', description: 'Message with URL or phone number buttons', icon: MousePointerClick, color: 'text-orange-600 bg-orange-50' },
  { key: 'twilio/quick-reply', label: 'Quick Reply', description: 'Message with quick reply button options', icon: MessageCircle, color: 'text-pink-600 bg-pink-50' },
  { key: 'twilio/card', label: 'Card', description: 'Rich card with media, title, body, and buttons', icon: LayoutTemplate, color: 'text-indigo-600 bg-indigo-50' },
  { key: 'twilio/carousel', label: 'Carousel', description: 'Multiple cards in a horizontal scroll', icon: Layers, color: 'text-cyan-600 bg-cyan-50' },
  { key: 'twilio/whatsapp-card', label: 'WhatsApp Card', description: 'WhatsApp-native card with header and buttons', icon: LayoutTemplate, color: 'text-emerald-600 bg-emerald-50' },
  { key: 'twilio/authentication', label: 'Authentication', description: 'OTP / verification code template', icon: Shield, color: 'text-red-600 bg-red-50' },
  { key: 'twilio/catalog', label: 'Catalog', description: 'WhatsApp product catalog message', icon: ShoppingCart, color: 'text-amber-600 bg-amber-50' },
  { key: 'twilio/flows', label: 'Flows', description: 'Trigger a WhatsApp Flow from a message', icon: Workflow, color: 'text-violet-600 bg-violet-50' },
];

// ─── Form State ──────────────────────────────────────────────────────────────

interface CtaButton { type: 'URL' | 'PHONE_NUMBER'; text: string; value: string; }
interface QuickReplyButton { id: string; title: string; }
interface ListItem { id: string; item: string; description?: string; }
interface CardButton { type: 'URL' | 'PHONE_NUMBER' | 'QUICK_REPLY'; text: string; value?: string; }
interface CarouselCard { title: string; body: string; media?: string; buttons: CardButton[]; }

interface TemplateForm {
  label: string;
  name: string;
  category: string;
  language: string;
  contentType: ContentTypeKey;
  // Text / shared body
  body: string;
  // Media
  mediaUrl: string;
  mediaContentType: string;
  // List Picker
  listBody: string;
  listItems: ListItem[];
  listButton: string;
  // Call to Action
  ctaBody: string;
  ctaButtons: CtaButton[];
  // Quick Reply
  qrBody: string;
  qrButtons: QuickReplyButton[];
  // Card / WhatsApp Card
  cardTitle: string;
  cardBody: string;
  cardMedia: string;
  cardButtons: CardButton[];
  // Carousel
  carouselCards: CarouselCard[];
  // Authentication
  authCode: string;
  authExpiry: string;
  // Catalog
  catalogBody: string;
  catalogId: string;
  // Flows
  flowsBody: string;
  flowSid: string;
  flowButton: string;
  // Sample values for {{N}} variables (key = variable index string e.g. "1", "2")
  sampleValues: Record<string, string>;
}

const emptyForm: TemplateForm = {
  label: '', name: '', category: 'MARKETING', language: 'en',
  contentType: 'twilio/text',
  body: '',
  mediaUrl: '', mediaContentType: 'image/jpeg',
  listBody: '', listItems: [{ id: '1', item: '', description: '' }], listButton: 'Select',
  ctaBody: '', ctaButtons: [{ type: 'URL', text: '', value: '' }],
  qrBody: '', qrButtons: [{ id: '1', title: '' }],
  cardTitle: '', cardBody: '', cardMedia: '', cardButtons: [{ type: 'URL', text: '', value: '' }],
  carouselCards: [{ title: '', body: '', media: '', buttons: [{ type: 'URL', text: '', value: '' }] }],
  authCode: '', authExpiry: '10',
  catalogBody: '', catalogId: '',
  flowsBody: '', flowSid: '', flowButton: 'Open Flow',
  sampleValues: {},
};

interface DripStep {
  day: number;
  template: string;
  description: string;
}

const dripSequences: Record<string, DripStep[]> = {
  'camp-001': [
    { day: 0, template: 'welcome_message', description: 'Welcome + NEET score congratulations' },
    { day: 1, template: 'mbbs_guidance', description: 'MBBS in India — eligibility & process' },
    { day: 3, template: 'college_options', description: 'Top college options for 600+ score' },
    { day: 5, template: 'counseling_invite', description: 'Free counseling session invite' },
    { day: 7, template: 'application_help', description: 'Application documentation guide' },
  ],
  'camp-002': [
    { day: 0, template: 'welcome_message', description: 'Welcome + abroad MBBS intro' },
    { day: 1, template: 'country_guide', description: 'Russia, Kyrgyzstan, Kazakhstan overview' },
    { day: 3, template: 'college_options', description: 'MCI-approved colleges abroad' },
    { day: 5, template: 'counseling_invite', description: 'Schedule abroad counseling session' },
  ],
};

const statusConfig: Record<CampaignStatus, { bg: string; text: string; dot: string }> = {
  Active: { bg: 'bg-green-50', text: 'text-green-700', dot: 'bg-green-500' },
  Paused: { bg: 'bg-amber-50', text: 'text-amber-700', dot: 'bg-amber-500' },
  Draft: { bg: 'bg-muted', text: 'text-muted-foreground', dot: 'bg-slate-400' },
  Completed: { bg: 'bg-blue-50', text: 'text-blue-700', dot: 'bg-blue-500' },
};

const CATEGORIES = ['MARKETING', 'UTILITY', 'AUTHENTICATION'];
const LANGUAGES = [
  { code: 'en', label: 'English' },
  { code: 'hi', label: 'Hindi' },
  { code: 'en_IN', label: 'English (India)' },
];

// ─── Helper: extract {{N}} variable indices from a body string ───────────────
function extractVariableIndices(text: string): string[] {
  const found = new Set<string>();
  const regex = /\{\{(\d+)\}\}/g;
  let match: RegExpExecArray | null;
  while ((match = regex.exec(text)) !== null) {
    found.add(match[1]);
  }
  return Array.from(found).sort((a, b) => Number(a) - Number(b));
}

// ─── Helper: derive a preview body string from form ──────────────────────────
function getPreviewBody(form: TemplateForm): string {
  switch (form.contentType) {
    case 'twilio/text': return form.body;
    case 'twilio/media': return form.body || form.mediaUrl || '(media message)';
    case 'twilio/list-picker': return form.listBody;
    case 'twilio/call-to-action': return form.ctaBody;
    case 'twilio/quick-reply': return form.qrBody;
    case 'twilio/card': return form.cardBody;
    case 'twilio/carousel': return form.carouselCards[0]?.body ?? '';
    case 'twilio/whatsapp-card': return form.cardBody;
    case 'twilio/authentication': return `Your verification code is {{1}}. Valid for ${form.authExpiry} minutes.`;
    case 'twilio/catalog': return form.catalogBody;
    case 'twilio/flows': return form.flowsBody;
    default: return form.body;
  }
}

export default function CampaignManagementClient() {
  const [activeCampaignId, setActiveCampaignId] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<'campaigns' | 'templates' | 'trackable-links'>('campaigns');

  const [templates, setTemplates] = useState<WaTemplate[]>([]);
  const [loadingTemplates, setLoadingTemplates] = useState(false);
  const [submittingId, setSubmittingId] = useState<string | null>(null);
  const [syncingId, setSyncingId] = useState<string | null>(null);
  const [submittingAll, setSubmittingAll] = useState(false);

  // Modal state — step 1: pick type, step 2: fill fields
  const [showModal, setShowModal] = useState(false);
  const [modalStep, setModalStep] = useState<1 | 2>(1);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState<TemplateForm>(emptyForm);
  const [formErrors, setFormErrors] = useState<Partial<Record<string, string>>>({});
  const [editingTemplateId, setEditingTemplateId] = useState<string | null>(null);

  const [previewTpl, setPreviewTpl] = useState<WaTemplate | null>(null);

  // ── Delete rejected template state ──────────────────────────────────────
  const [deletingTpl, setDeletingTpl] = useState<WaTemplate | null>(null);
  const [confirmingDelete, setConfirmingDelete] = useState(false);

  // ── Edit Campaign state ──────────────────────────────────────────────────
  const [campaignList, setCampaignList] = useState<Campaign[]>([]);
  const [loadingCampaigns, setLoadingCampaigns] = useState(false);
  const [editingCampaign, setEditingCampaign] = useState<Campaign | null>(null);
  const [editForm, setEditForm] = useState<Partial<Campaign>>({});
  const [editErrors, setEditErrors] = useState<Partial<Record<string, string>>>({});
  const [editSaving, setEditSaving] = useState(false);

  // ── Create Campaign state ────────────────────────────────────────────────
  const [showCreateCampaign, setShowCreateCampaign] = useState(false);
  const [createForm, setCreateForm] = useState<Partial<Campaign>>({});
  const [createErrors, setCreateErrors] = useState<Partial<Record<string, string>>>({});
  const [createSaving, setCreateSaving] = useState(false);

  // ── Drip Sequence Editor state ───────────────────────────────────────────
  const [dripState, setDripState] = useState<Record<string, DripStep[]>>(dripSequences);
  const [editingDripCampaignId, setEditingDripCampaignId] = useState<string | null>(null);
  const [dripEditSteps, setDripEditSteps] = useState<DripStep[]>([]);
  const [editingStepIdx, setEditingStepIdx] = useState<number | null>(null);
  const [stepForm, setStepForm] = useState<DripStep>({ day: 0, template: '', description: '' });
  const [stepErrors, setStepErrors] = useState<Partial<Record<string, string>>>({});
  const [savingDrip, setSavingDrip] = useState(false);

  const supabase = createClient();

  const fetchTemplates = useCallback(async () => {
    setLoadingTemplates(true);
    try {
      const { data, error } = await supabase
        .from('whatsapp_templates')
        .select('*')
        .order('created_at', { ascending: false });
      if (error) throw error;
      setTemplates(data ?? []);
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : 'Failed to load templates';
      toast.error(msg);
    } finally {
      setLoadingTemplates(false);
    }
  }, [supabase]);

  const fetchDripSequences = useCallback(async () => {
    try {
      const { data, error } = await supabase
        .from('campaign_drip_sequences')
        .select('*')
        .order('campaign_id')
        .order('step_order');
      if (error) throw error;
      if (data && data.length > 0) {
        const grouped: Record<string, DripStep[]> = {};
        data.forEach((row: { campaign_id: string; day: number; template: string; description: string }) => {
          if (!grouped[row.campaign_id]) grouped[row.campaign_id] = [];
          grouped[row.campaign_id].push({ day: row.day, template: row.template, description: row.description });
        });
        setDripState(grouped);
      }
    } catch {
      // silently fall back to local state
    }
  }, [supabase]);

  const fetchCampaigns = useCallback(async () => {
    setLoadingCampaigns(true);
    try {
      // First try to load from campaigns table
      const { data: campaignsData, error: campaignsError } = await supabase
        .from('campaigns')
        .select('*')
        .order('created_at', { ascending: false });

      if (!campaignsError && campaignsData && campaignsData.length > 0) {
        const mapped: Campaign[] = campaignsData.map((row: {
          id: string; name: string; status: string; segment: string;
          enrolled: number; sent: number; delivered: number; opened: number;
          delivery_rate: string; open_rate: string; start_date: string; drip_days: number;
        }) => ({
          id: row.id,
          name: row.name,
          status: row.status as CampaignStatus,
          segment: row.segment,
          enrolled: row.enrolled,
          sent: row.sent,
          delivered: row.delivered,
          opened: row.opened,
          deliveryRate: row.delivery_rate,
          openRate: row.open_rate,
          startDate: row.start_date,
          dripDays: row.drip_days,
        }));
        setCampaignList(mapped);
        if (mapped.length > 0 && !activeCampaignId) {
          setActiveCampaignId(mapped[0].id);
        }
        setLoadingCampaigns(false);
        return;
      }

      // Fallback: Get unique campaigns from campaign_drip_sequences
      const { data: dripData, error: dripError } = await supabase
        .from('campaign_drip_sequences')
        .select('campaign_id, campaign_name, day')
        .order('campaign_id');
      if (dripError) throw dripError;

      if (!dripData || dripData.length === 0) {
        setCampaignList([]);
        setLoadingCampaigns(false);
        return;
      }

      // Group by campaign_id to get unique campaigns + compute dripDays
      const campaignMap: Record<string, { name: string; maxDay: number; stepCount: number }> = {};
      dripData.forEach((row: { campaign_id: string; campaign_name: string; day: number }) => {
        if (!campaignMap[row.campaign_id]) {
          campaignMap[row.campaign_id] = { name: row.campaign_name, maxDay: row.day, stepCount: 1 };
        } else {
          campaignMap[row.campaign_id].stepCount++;
          if (row.day > campaignMap[row.campaign_id].maxDay) {
            campaignMap[row.campaign_id].maxDay = row.day;
          }
        }
      });

      // Get stats from drip_message_queue per campaign
      const { data: queueData } = await supabase
        .from('drip_message_queue')
        .select('campaign_id, status, lead_phone');

      const statsMap: Record<string, { sent: number; delivered: number; phones: Set<string> }> = {};
      (queueData ?? []).forEach((row: { campaign_id: string; status: string; lead_phone: string }) => {
        if (!statsMap[row.campaign_id]) {
          statsMap[row.campaign_id] = { sent: 0, delivered: 0, phones: new Set() };
        }
        statsMap[row.campaign_id].phones.add(row.lead_phone);
        if (row.status === 'sent') {
          statsMap[row.campaign_id].sent++;
          statsMap[row.campaign_id].delivered++;
        }
      });

      const derived: Campaign[] = Object.entries(campaignMap).map(([campId, info]) => {
        const stats = statsMap[campId];
        const sent = stats?.sent ?? 0;
        const delivered = stats?.delivered ?? 0;
        const enrolled = stats?.phones.size ?? 0;
        const deliveryRate = sent > 0 ? `${((delivered / sent) * 100).toFixed(1)}%` : '—';
        return {
          id: campId,
          name: info.name,
          status: 'Active' as CampaignStatus,
          segment: '',
          enrolled,
          sent,
          delivered,
          opened: 0,
          deliveryRate,
          openRate: '—',
          startDate: '—',
          dripDays: info.maxDay,
        };
      });

      setCampaignList(derived);
      if (derived.length > 0 && !activeCampaignId) {
        setActiveCampaignId(derived[0].id);
      }
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : 'Failed to load campaigns';
      toast.error(msg);
    } finally {
      setLoadingCampaigns(false);
    }
  }, [supabase, activeCampaignId]);

  useEffect(() => {
    if (activeTab === 'templates') fetchTemplates();
  }, [activeTab, fetchTemplates]);

  useEffect(() => {
    fetchDripSequences();
    fetchCampaigns();
  }, [fetchDripSequences, fetchCampaigns]);

    // campaigns are fetched from DB — no localStorage persistence needed

  const slugify = (str: string) =>
    str.toLowerCase().trim().replace(/[^a-z0-9\s_]/g, '').replace(/\s+/g, '_');

  const handleLabelChange = (val: string) => {
    setForm((f) => ({ ...f, label: val, name: slugify(val) }));
    setFormErrors((e) => ({ ...e, label: '', name: '' }));
  };

  const validateStep2 = (): boolean => {
    const errors: Partial<Record<string, string>> = {};
    if (!form.label.trim()) errors.label = 'Template label is required';
    if (!form.name.trim()) errors.name = 'Template key is required';
    if (!/^[a-z0-9_]+$/.test(form.name)) errors.name = 'Only lowercase letters, numbers, underscores';

    switch (form.contentType) {
      case 'twilio/text':
        if (!form.body.trim()) errors.body = 'Message body is required';
        if (form.body.length > 1024) errors.body = 'Body must be under 1024 characters';
        break;
      case 'twilio/media':
        if (!form.mediaUrl.trim()) errors.mediaUrl = 'Media URL is required';
        break;
      case 'twilio/list-picker':
        if (!form.listBody.trim()) errors.listBody = 'Body is required';
        if (form.listItems.some((i) => !i.item.trim())) errors.listItems = 'All list items need a title';
        else if (form.listItems.some((i) => i.item.length > 24)) errors.listItems = 'Item titles over 24 characters will be auto-truncated (Twilio limit).';
        break;
      case 'twilio/call-to-action':
        if (!form.ctaBody.trim()) errors.ctaBody = 'Body is required';
        if (form.ctaButtons.some((b) => !b.text.trim() || !b.value.trim())) errors.ctaButtons = 'All buttons need text and value';
        break;
      case 'twilio/quick-reply':
        if (!form.qrBody.trim()) errors.qrBody = 'Body is required';
        if (form.qrButtons.some((b) => !b.title.trim())) errors.qrButtons = 'All buttons need a title';
        break;
      case 'twilio/card': case'twilio/whatsapp-card':
        if (!form.cardTitle.trim()) errors.cardTitle = 'Card title is required';
        if (!form.cardBody.trim()) errors.cardBody = 'Card body is required';
        break;
      case 'twilio/carousel':
        if (form.carouselCards.some((c) => !c.title.trim() || !c.body.trim())) errors.carouselCards = 'All cards need title and body';
        break;
      case 'twilio/authentication':
        break;
      case 'twilio/catalog':
        if (!form.catalogBody.trim()) errors.catalogBody = 'Body is required';
        break;
      case 'twilio/flows':
        if (!form.flowsBody.trim()) errors.flowsBody = 'Body is required';
        if (!form.flowSid.trim()) errors.flowSid = 'Flow SID is required';
        break;
    }
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSave = async () => {
    if (!validateStep2()) return;
    setSaving(true);
    try {
      const bodyForDb = getPreviewBody(form);
      const sampleVals = form.sampleValues && Object.keys(form.sampleValues).length > 0
        ? JSON.parse(JSON.stringify(form.sampleValues))
        : null;

      // Build the full form payload for non-text types so we can resubmit correctly later
      const formPayload: Record<string, unknown> = {
        contentType: form.contentType,
        body: form.body,
        mediaUrl: form.mediaUrl,
        mediaContentType: form.mediaContentType,
        listBody: form.listBody,
        listItems: form.listItems,
        listButton: form.listButton,
        ctaBody: form.ctaBody,
        ctaButtons: form.ctaButtons,
        qrBody: form.qrBody,
        qrButtons: form.qrButtons,
        cardTitle: form.cardTitle,
        cardBody: form.cardBody,
        cardMedia: form.cardMedia,
        cardButtons: form.cardButtons,
        carouselCards: form.carouselCards,
        authCode: form.authCode,
        authExpiry: form.authExpiry,
        catalogBody: form.catalogBody,
        catalogId: form.catalogId,
        flowsBody: form.flowsBody,
        flowSid: form.flowSid,
        flowButton: form.flowButton,
      };

      if (editingTemplateId) {
        const { error } = await supabase.from('whatsapp_templates').update({
          name: form.name,
          label: form.label,
          body: bodyForDb,
          category: form.category,
          language: form.language,
          sample_values: sampleVals,
          content_type: form.contentType,
          media_url: form.contentType === 'twilio/media' ? form.mediaUrl : null,
          media_content_type: form.contentType === 'twilio/media' ? form.mediaContentType : null,
          form_payload: formPayload,
          // Reset approval status so it goes back for re-approval
          status: 'Pending',
          twilio_approval_status: null,
          content_sid: null,
          submitted_at: null,
        }).eq('id', editingTemplateId);
        if (error) {
          if (error.code === '23505') {
            setFormErrors({ name: 'A template with this key already exists' });
            return;
          }
          throw error;
        }
        toast.success(`Template "${form.label}" updated — resubmitting for approval…`);

        // Auto-resubmit the updated template for Twilio approval
        try {
          const approvalRes = await fetch('/api/twilio/template-approval', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              templateId: editingTemplateId,
              name: form.name,
              label: form.label,
              body: bodyForDb,
              category: form.category,
              language: form.language,
              contentType: form.contentType,
              sampleValues: form.sampleValues ?? {},
              ...formPayload,
            }),
          });
          const approvalData = await approvalRes.json() as { success?: boolean; displayStatus?: string; error?: string };
          if (approvalRes.ok && approvalData.success) {
            toast.success(`"${form.label}" resubmitted to Twilio — status: ${approvalData.displayStatus}`);
          } else {
            toast.warning(`Template updated but resubmission failed: ${approvalData.error ?? 'Unknown error'}. You can manually resubmit from the templates list.`);
          }
        } catch {
          toast.warning(`Template updated but auto-resubmission failed. You can manually resubmit from the templates list.`);
        }
      } else {
        const { error } = await supabase.from('whatsapp_templates').insert({
          name: form.name,
          label: form.label,
          body: bodyForDb,
          category: form.category,
          language: form.language,
          status: 'Pending',
          uses: 0,
          sample_values: sampleVals,
          content_type: form.contentType,
          media_url: form.contentType === 'twilio/media' ? form.mediaUrl : null,
          media_content_type: form.contentType === 'twilio/media' ? form.mediaContentType : null,
          form_payload: formPayload,
        });
        if (error) {
          if (error.code === '23505') {
            setFormErrors({ name: 'A template with this key already exists' });
            return;
          }
          throw error;
        }
        toast.success(`Template "${form.label}" created successfully`);
      }
      setShowModal(false);
      setForm(emptyForm);
      setFormErrors({});
      setModalStep(1);
      setEditingTemplateId(null);
      fetchTemplates();
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : 'Failed to save template';
      toast.error(msg);
    } finally {
      setSaving(false);
    }
  };

  const handleSubmitForApproval = async (tpl: WaTemplate) => {
    if (submittingId) return;
    setSubmittingId(tpl.id);
    try {
      // Use stored content_type and form_payload so media/complex templates submit correctly
      const storedContentType = tpl.content_type ?? 'twilio/text';
      const storedPayload = tpl.form_payload ?? {};
      const res = await fetch('/api/twilio/template-approval', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          templateId: tpl.id,
          name: tpl.name,
          label: tpl.label,
          body: tpl.body,
          category: tpl.category,
          language: tpl.language,
          contentType: storedContentType,
          sampleValues: tpl.sample_values ?? {},
          ...storedPayload,
        }),
      });
      const data = await res.json() as { success?: boolean; contentSid?: string; displayStatus?: string; error?: string };
      if (!res.ok || !data.success) {
        toast.error(data.error ?? 'Failed to submit for approval');
        return;
      }
      toast.success(`"${tpl.label}" submitted to Twilio — status: ${data.displayStatus}`);
      fetchTemplates();
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Submission failed';
      toast.error(msg);
    } finally {
      setSubmittingId(null);
    }
  };

  const handleSubmitAllForApproval = async () => {
    const pending = templates.filter((t) => !t.content_sid);
    if (pending.length === 0) {
      toast.info('No pending templates to submit — all are already submitted to Twilio.');
      return;
    }
    setSubmittingAll(true);
    let successCount = 0;
    let failCount = 0;
    for (const tpl of pending) {
      try {
        // Use stored content_type and form_payload so media/complex templates submit correctly
        const storedContentType = tpl.content_type ?? 'twilio/text';
        const storedPayload = tpl.form_payload ?? {};
        const res = await fetch('/api/twilio/template-approval', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            templateId: tpl.id,
            name: tpl.name,
            label: tpl.label,
            body: tpl.body,
            category: tpl.category ?? 'MARKETING',
            language: tpl.language ?? 'en',
            contentType: storedContentType,
            sampleValues: tpl.sample_values ?? {},
            ...storedPayload,
          }),
        });
        const data = await res.json() as { success?: boolean; displayStatus?: string; error?: string };
        if (res.ok && data.success) {
          successCount++;
        } else {
          failCount++;
          console.error(`Failed to submit "${tpl.label}":`, data.error);
        }
      } catch {
        failCount++;
      }
    }
    setSubmittingAll(false);
    if (successCount > 0 && failCount === 0) {
      toast.success(`${successCount} template${successCount > 1 ? 's' : ''} submitted to Twilio for WhatsApp approval.`);
    } else if (successCount > 0 && failCount > 0) {
      toast.warning(`${successCount} submitted, ${failCount} failed. Check individual templates for details.`);
    } else {
      toast.error(`All ${failCount} submission${failCount > 1 ? 's' : ''} failed. Check Twilio credentials.`);
    }
    fetchTemplates();
  };

  const handleSyncStatus = async (tpl: WaTemplate) => {
    if (!tpl.content_sid || syncingId) return;
    setSyncingId(tpl.id);
    try {
      const res = await fetch(
        `/api/twilio/template-approval?templateId=${encodeURIComponent(tpl.id)}&contentSid=${encodeURIComponent(tpl.content_sid)}`,
        { cache: 'no-store' }
      );
      const data = await res.json() as { success?: boolean; displayStatus?: string; twilioStatus?: string; error?: string };
      if (!res.ok || !data.success) {
        toast.error(data.error ?? 'Failed to sync status');
        return;
      }
      toast.success(`Status synced: ${data.displayStatus}`);
      fetchTemplates();
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Sync failed';
      toast.error(msg);
    } finally {
      setSyncingId(null);
    }
  };

  const handleDeleteRejectedTemplate = async () => {
    if (!deletingTpl) return;
    setConfirmingDelete(true);
    try {
      const { error } = await supabase
        .from('whatsapp_templates')
        .delete()
        .eq('id', deletingTpl.id);
      if (error) throw error;
      toast.success(`Template "${deletingTpl.label}" deleted`);
      setDeletingTpl(null);
      fetchTemplates();
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : 'Failed to delete template';
      toast.error(msg);
    } finally {
      setConfirmingDelete(false);
    }
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setForm(emptyForm);
    setFormErrors({});
    setModalStep(1);
    setEditingTemplateId(null);
  };

  const handleOpenEdit = (camp: Campaign) => {
    setEditingCampaign(camp);
    setEditForm({
      name: camp.name,
      status: camp.status,
      segment: camp.segment,
      dripDays: camp.dripDays,
      startDate: camp.startDate,
    });
    setEditErrors({});
  };

  const handleCloseEdit = () => {
    setEditingCampaign(null);
    setEditForm({});
    setEditErrors({});
  };

  const handleOpenCreateCampaign = () => {
    setCreateForm({ status: 'Draft', dripDays: 7, startDate: '' });
    setCreateErrors({});
    setShowCreateCampaign(true);
  };

  const handleCloseCreateCampaign = () => {
    setShowCreateCampaign(false);
    setCreateForm({});
    setCreateErrors({});
  };

  const validateCreateForm = (): boolean => {
    const errors: Partial<Record<string, string>> = {};
    if (!createForm.name?.trim()) errors.name = 'Campaign name is required';
    if (!createForm.segment?.trim()) errors.segment = 'Segment is required';
    if (!createForm.dripDays || createForm.dripDays < 1) errors.dripDays = 'Drip days must be at least 1';
    setCreateErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleCreateSave = async () => {
    if (!validateCreateForm()) return;
    setCreateSaving(true);
    try {
      const newId = `camp-${Date.now()}`;
      const newCampaign: Campaign = {
        id: newId,
        name: createForm.name ?? 'New Campaign',
        status: (createForm.status as CampaignStatus) ?? 'Draft',
        segment: createForm.segment ?? '',
        enrolled: 0,
        sent: 0,
        delivered: 0,
        opened: 0,
        deliveryRate: '0%',
        openRate: '0%',
        startDate: createForm.startDate ?? '',
        dripDays: createForm.dripDays ?? 7,
      };

      const { error } = await supabase.from('campaigns').insert({
        id: newCampaign.id,
        name: newCampaign.name,
        status: newCampaign.status,
        segment: newCampaign.segment,
        enrolled: newCampaign.enrolled,
        sent: newCampaign.sent,
        delivered: newCampaign.delivered,
        opened: newCampaign.opened,
        delivery_rate: newCampaign.deliveryRate,
        open_rate: newCampaign.openRate,
        start_date: newCampaign.startDate,
        drip_days: newCampaign.dripDays,
      });

      if (error) throw error;

      setCampaignList((prev) => [newCampaign, ...prev]);
      setActiveCampaignId(newCampaign.id);
      toast.success(`Campaign "${newCampaign.name}" created successfully`);
      handleCloseCreateCampaign();
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : 'Failed to create campaign';
      toast.error(msg);
    } finally {
      setCreateSaving(false);
    }
  };

  const validateEditForm = (): boolean => {
    const errors: Partial<Record<string, string>> = {};
    if (!editForm.name?.trim()) errors.name = 'Campaign name is required';
    if (!editForm.segment?.trim()) errors.segment = 'Segment is required';
    if (!editForm.dripDays || editForm.dripDays < 1) errors.dripDays = 'Drip days must be at least 1';
    setEditErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleEditSave = async () => {
    if (!validateEditForm() || !editingCampaign) return;
    setEditSaving(true);
    try {
      const { error } = await supabase
        .from('campaigns')
        .update({
          name: editForm.name ?? editingCampaign.name,
          status: editForm.status ?? editingCampaign.status,
          segment: editForm.segment ?? editingCampaign.segment,
          drip_days: editForm.dripDays ?? editingCampaign.dripDays,
          start_date: editForm.startDate ?? editingCampaign.startDate,
          updated_at: new Date().toISOString(),
        })
        .eq('id', editingCampaign.id);

      if (error) throw error;

      setCampaignList((prev) =>
        prev.map((c) =>
          c.id === editingCampaign.id
            ? {
                ...c,
                name: editForm.name ?? c.name,
                status: editForm.status ?? c.status,
                segment: editForm.segment ?? c.segment,
                dripDays: editForm.dripDays ?? c.dripDays,
                startDate: editForm.startDate ?? c.startDate,
              }
            : c
        )
      );
      toast.success(`Campaign "${editForm.name}" updated successfully`);
      handleCloseEdit();
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : 'Failed to update campaign';
      toast.error(msg);
    } finally {
      setEditSaving(false);
    }
  };

  const handleOpenEditTemplate = (tpl: WaTemplate) => {
    // Restore full form state from stored form_payload if available
    const stored = tpl.form_payload as Partial<TemplateForm> | null | undefined;
    setForm({
      ...emptyForm,
      label: tpl.label,
      name: tpl.name,
      category: tpl.category || 'MARKETING',
      language: tpl.language || 'en',
      contentType: (tpl.content_type as ContentTypeKey) ?? 'twilio/text',
      body: stored?.body ?? tpl.body,
      mediaUrl: stored?.mediaUrl ?? tpl.media_url ?? '',
      mediaContentType: stored?.mediaContentType ?? tpl.media_content_type ?? 'image/jpeg',
      listBody: stored?.listBody ?? '',
      listItems: stored?.listItems ?? [{ id: '1', item: '', description: '' }],
      listButton: stored?.listButton ?? 'Select',
      ctaBody: stored?.ctaBody ?? '',
      ctaButtons: stored?.ctaButtons ?? [{ type: 'URL', text: '', value: '' }],
      qrBody: stored?.qrBody ?? '',
      qrButtons: stored?.qrButtons ?? [{ id: '1', title: '' }],
      cardTitle: stored?.cardTitle ?? '',
      cardBody: stored?.cardBody ?? '',
      cardMedia: stored?.cardMedia ?? '',
      cardButtons: stored?.cardButtons ?? [{ type: 'URL', text: '', value: '' }],
      carouselCards: stored?.carouselCards ?? [{ title: '', body: '', media: '', buttons: [{ type: 'URL', text: '', value: '' }] }],
      authCode: stored?.authCode ?? '',
      authExpiry: stored?.authExpiry ?? '10',
      catalogBody: stored?.catalogBody ?? '',
      catalogId: stored?.catalogId ?? '',
      flowsBody: stored?.flowsBody ?? '',
      flowSid: stored?.flowSid ?? '',
      flowButton: stored?.flowButton ?? 'Open Flow',
      sampleValues: tpl.sample_values ?? {},
    });
    setFormErrors({});
    setEditingTemplateId(tpl.id);
    setModalStep(2);
    setShowModal(true);
  };

  const selectedCampaign = campaignList.find((c) => c.id === activeCampaignId);
  const drip = activeCampaignId ? dripState[activeCampaignId] ?? [] : [];
  const selectedTypeInfo = CONTENT_TYPES.find((t) => t.key === form.contentType);

  // ─── Drip Sequence Editor handlers ──────────────────────────────────────
  const handleOpenDripEditor = (campId: string) => {
    setEditingDripCampaignId(campId);
    setDripEditSteps([...(dripState[campId] ?? [])]);
    setEditingStepIdx(null);
    setStepForm({ day: 0, template: '', description: '' });
    setStepErrors({});
  };

  const handleCloseDripEditor = () => {
    setEditingDripCampaignId(null);
    setDripEditSteps([]);
    setEditingStepIdx(null);
    setStepErrors({});
  };

  const handleMoveStep = (idx: number, dir: 'up' | 'down') => {
    const steps = [...dripEditSteps];
    const swapIdx = dir === 'up' ? idx - 1 : idx + 1;
    if (swapIdx < 0 || swapIdx >= steps.length) return;
    [steps[idx], steps[swapIdx]] = [steps[swapIdx], steps[idx]];
    setDripEditSteps(steps);
  };

  const handleDeleteStep = (idx: number) => {
    setDripEditSteps((prev) => prev.filter((_, i) => i !== idx));
    if (editingStepIdx === idx) {
      setEditingStepIdx(null);
      setStepForm({ day: 0, template: '', description: '' });
    }
  };

  const handleEditStep = (idx: number) => {
    setEditingStepIdx(idx);
    setStepForm({ ...dripEditSteps[idx] });
    setStepErrors({});
  };

  const handleAddNewStep = () => {
    setEditingStepIdx(-1); // -1 = new step
    setStepForm({ day: 0, template: '', description: '' });
    setStepErrors({});
  };

  const validateStepForm = (): boolean => {
    const errors: Partial<Record<string, string>> = {};
    if (stepForm.day < 0) errors.day = 'Day must be 0 or greater';
    if (!stepForm.template.trim()) errors.template = 'Template key is required';
    if (!stepForm.description.trim()) errors.description = 'Description is required';
    setStepErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSaveStep = () => {
    if (!validateStepForm()) return;
    if (editingStepIdx === -1) {
      // Add new
      setDripEditSteps((prev) => [...prev, { ...stepForm }]);
    } else if (editingStepIdx !== null) {
      // Update existing
      setDripEditSteps((prev) => prev.map((s, i) => (i === editingStepIdx ? { ...stepForm } : s)));
    }
    setEditingStepIdx(null);
    setStepForm({ day: 0, template: '', description: '' });
    setStepErrors({});
  };

  const handleSaveDripSequence = async () => {
    if (!editingDripCampaignId) return;
    setSavingDrip(true);
    try {
      const campaignName = campaignList.find((c) => c.id === editingDripCampaignId)?.name ?? editingDripCampaignId;
      // Delete existing steps for this campaign
      await supabase.from('campaign_drip_sequences').delete().eq('campaign_id', editingDripCampaignId);
      // Insert updated steps
      if (dripEditSteps.length > 0) {
        const rows = dripEditSteps.map((step, idx) => ({
          campaign_id: editingDripCampaignId,
          campaign_name: campaignName,
          step_order: idx,
          day: step.day,
          template: step.template,
          description: step.description,
        }));
        const { error } = await supabase.from('campaign_drip_sequences').insert(rows);
        if (error) throw error;
      }
      setDripState((prev) => ({ ...prev, [editingDripCampaignId]: dripEditSteps }));
      toast.success('Drip sequence updated successfully');
      handleCloseDripEditor();
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Failed to save drip sequence';
      toast.error(msg);
    } finally {
      setSavingDrip(false);
    }
  };

  // ─── Shared input classes ────────────────────────────────────────────────
  const inputCls = (err?: string) =>
    `w-full px-3 py-2 text-sm border rounded-lg bg-background text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/30 transition-colors ${err ? 'border-red-400' : 'border-border'}`;

  // ─── Sample value inputs for {{N}} variables ─────────────────────────────
  const renderSampleValueInputs = (bodyText: string) => {
    const indices = extractVariableIndices(bodyText);
    if (indices.length === 0) return null;
    return (
      <div className="bg-amber-50 border border-amber-200 rounded-lg px-3 py-3 space-y-2">
        <p className="text-xs font-semibold text-amber-800 flex items-center gap-1.5">
          <span className="inline-block w-4 h-4 rounded-full bg-amber-500 text-white text-[10px] font-bold flex items-center justify-center">!</span>
          Sample values required for Meta approval
        </p>
        <p className="text-xs text-amber-700">Provide example content for each variable. These are shown to Meta reviewers and must represent real data.</p>
        <div className="space-y-2 mt-1">
          {indices.map((idx) => (
            <div key={`sample-${idx}`} className="flex items-center gap-2">
              <span className="text-xs font-mono font-semibold text-amber-800 w-8 shrink-0">{`{{${idx}}}`}</span>
              <input
                type="text"
                value={form.sampleValues[idx] ?? ''}
                onChange={(e) => {
                  const val = e.target.value;
                  setForm((f) => ({ ...f, sampleValues: { ...f.sampleValues, [idx]: val } }));
                  setFormErrors((er) => ({ ...er, [`sample_${idx}`]: '' }));
                }}
                placeholder={`Sample value for {{${idx}}} e.g. "Rahul Sharma"`}
                className={inputCls(formErrors[`sample_${idx}`])}
              />
            </div>
          ))}
        </div>
        {indices.some((idx) => formErrors[`sample_${idx}`]) && (
          <p className="text-xs text-red-500">All variable sample values are required before submitting for approval.</p>
        )}
      </div>
    );
  };

  // ─── Step 2 field renderers ──────────────────────────────────────────────
  const renderTypeFields = () => {
    switch (form.contentType) {
      // ── Text ──────────────────────────────────────────────────────────────
      case 'twilio/text':
        return (
          <div className="space-y-3">
            <div>
              <label className="block text-xs font-semibold text-foreground mb-1.5">Message Body <span className="text-red-500">*</span></label>
              <textarea
                value={form.body}
                onChange={(e) => { setForm((f) => ({ ...f, body: e.target.value })); setFormErrors((er) => ({ ...er, body: '' })); }}
                rows={4}
                placeholder="Hi {{1}}, your message here..."
                className={`${inputCls(formErrors.body)} resize-none`}
              />
              <div className="flex items-center justify-between mt-1">
                {formErrors.body ? <p className="text-xs text-red-500">{formErrors.body}</p> : <p className="text-xs text-muted-foreground">Use {'{{1}}'}, {'{{2}}'} for variables</p>}
                <span className={`text-xs tabular-nums ${form.body.length > 1024 ? 'text-red-500' : 'text-muted-foreground'}`}>{form.body.length}/1024</span>
              </div>
            </div>
            {renderSampleValueInputs(form.body)}
          </div>
        );

      // ── Media ─────────────────────────────────────────────────────────────
      case 'twilio/media':
        return (
          <div className="space-y-3">
            <div>
              <label className="block text-xs font-semibold text-foreground mb-1.5">Media URL <span className="text-red-500">*</span></label>
              <input type="url" value={form.mediaUrl} onChange={(e) => { setForm((f) => ({ ...f, mediaUrl: e.target.value })); setFormErrors((er) => ({ ...er, mediaUrl: '' })); }} placeholder="https://example.com/image.jpg" className={inputCls(formErrors.mediaUrl)} />
              {formErrors.mediaUrl && <p className="text-xs text-red-500 mt-1">{formErrors.mediaUrl}</p>}
            </div>
            <div>
              <label className="block text-xs font-semibold text-foreground mb-1.5">Media Type</label>
              <select value={form.mediaContentType} onChange={(e) => setForm((f) => ({ ...f, mediaContentType: e.target.value }))} className={inputCls()}>
                {['image/jpeg', 'image/png', 'video/mp4', 'application/pdf'].map((t) => <option key={t} value={t}>{t}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-xs font-semibold text-foreground mb-1.5">Caption (optional)</label>
              <textarea value={form.body} onChange={(e) => setForm((f) => ({ ...f, body: e.target.value }))} rows={2} placeholder="Optional caption text..." className={`${inputCls()} resize-none`} />
            </div>
          </div>
        );

      // ── List Picker ───────────────────────────────────────────────────────
      case 'twilio/list-picker':
        return (
          <div className="space-y-3">
            <div>
              <label className="block text-xs font-semibold text-foreground mb-1.5">Body <span className="text-red-500">*</span></label>
              <textarea value={form.listBody} onChange={(e) => { setForm((f) => ({ ...f, listBody: e.target.value })); setFormErrors((er) => ({ ...er, listBody: '' })); }} rows={2} placeholder="Please select an option:" className={`${inputCls(formErrors.listBody)} resize-none`} />
              {formErrors.listBody && <p className="text-xs text-red-500 mt-1">{formErrors.listBody}</p>}
            </div>
            {renderSampleValueInputs(form.listBody)}
            <div>
              <label className="block text-xs font-semibold text-foreground mb-1.5">Button Label</label>
              <input type="text" value={form.listButton} onChange={(e) => setForm((f) => ({ ...f, listButton: e.target.value }))} placeholder="Select" className={inputCls()} />
            </div>
            <div>
              <div className="flex items-center justify-between mb-1.5">
                <label className="text-xs font-semibold text-foreground">List Items <span className="text-red-500">*</span></label>
                <button type="button" onClick={() => setForm((f) => ({ ...f, listItems: [...f.listItems, { id: String(Date.now()), item: '', description: '' }] }))} className="text-xs text-primary hover:underline">+ Add item</button>
              </div>
              {formErrors.listItems && <p className="text-xs text-red-500 mb-1">{formErrors.listItems}</p>}
              <div className="space-y-2">
                {form.listItems.map((item, idx) => (
                  <div key={item.id} className="flex gap-2 items-start">
                    <div className="flex-1 space-y-1">
                      <div className="relative">
                        <input type="text" value={item.item} onChange={(e) => { const items = [...form.listItems]; items[idx] = { ...items[idx], item: e.target.value }; setForm((f) => ({ ...f, listItems: items })); setFormErrors((er) => ({ ...er, listItems: '' })); }} placeholder={`Item ${idx + 1} title`} maxLength={24} className={inputCls(item.item.length > 24 ? 'error' : '')} />
                        <span className={`absolute right-2 top-1/2 -translate-y-1/2 text-xs ${item.item.length > 20 ? (item.item.length >= 24 ? 'text-red-500' : 'text-amber-500') : 'text-muted-foreground'}`}>{item.item.length}/24</span>
                      </div>
                      <input type="text" value={item.description ?? ''} onChange={(e) => { const items = [...form.listItems]; items[idx] = { ...items[idx], description: e.target.value }; setForm((f) => ({ ...f, listItems: items })); }} placeholder="Description (optional)" className={inputCls()} />
                    </div>
                    {form.listItems.length > 1 && (
                      <button type="button" onClick={() => setForm((f) => ({ ...f, listItems: f.listItems.filter((_, i) => i !== idx) }))} className="mt-1 p-1.5 rounded hover:bg-red-50 text-muted-foreground hover:text-red-500 transition-colors"><X size={13} /></button>
                    )}
                  </div>
                ))}
              </div>
            </div>
          </div>
        );

      // ── Call to Action ────────────────────────────────────────────────────
      case 'twilio/call-to-action':
        return (
          <div className="space-y-3">
            <div>
              <label className="block text-xs font-semibold text-foreground mb-1.5">Body <span className="text-red-500">*</span></label>
              <textarea value={form.ctaBody} onChange={(e) => { setForm((f) => ({ ...f, ctaBody: e.target.value })); setFormErrors((er) => ({ ...er, ctaBody: '' })); }} rows={2} placeholder="Visit our website or call us..." className={`${inputCls(formErrors.ctaBody)} resize-none`} />
              {formErrors.ctaBody && <p className="text-xs text-red-500 mt-1">{formErrors.ctaBody}</p>}
            </div>
            {renderSampleValueInputs(form.ctaBody)}
            <div>
              <div className="flex items-center justify-between mb-1.5">
                <label className="text-xs font-semibold text-foreground">Buttons <span className="text-red-500">*</span></label>
                {form.ctaButtons.length < 2 && (
                  <button type="button" onClick={() => setForm((f) => ({ ...f, ctaButtons: [...f.ctaButtons, { type: 'URL', text: '', value: '' }] }))} className="text-xs text-primary hover:underline">+ Add button</button>
                )}
              </div>
              {formErrors.ctaButtons && <p className="text-xs text-red-500 mb-1">{formErrors.ctaButtons}</p>}
              <div className="space-y-2">
                {form.ctaButtons.map((btn, idx) => (
                  <div key={idx} className="border border-border rounded-lg p-3 space-y-2">
                    <div className="flex gap-2">
                      <select value={btn.type} onChange={(e) => { const btns = [...form.ctaButtons]; btns[idx] = { ...btns[idx], type: e.target.value as 'URL' | 'PHONE_NUMBER' }; setForm((f) => ({ ...f, ctaButtons: btns })); }} className={`${inputCls()} w-40`}>
                        <option value="URL">URL</option>
                        <option value="PHONE_NUMBER">Phone Number</option>
                      </select>
                      {form.ctaButtons.length > 1 && (
                        <button type="button" onClick={() => setForm((f) => ({ ...f, ctaButtons: f.ctaButtons.filter((_, i) => i !== idx) }))} className="p-1.5 rounded hover:bg-red-50 text-muted-foreground hover:text-red-500 transition-colors"><X size={13} /></button>
                      )}
                    </div>
                    <input type="text" value={btn.text} onChange={(e) => { const btns = [...form.ctaButtons]; btns[idx] = { ...btns[idx], text: e.target.value }; setForm((f) => ({ ...f, ctaButtons: btns })); }} placeholder="Button label" className={inputCls()} />
                    <input type="text" value={btn.value} onChange={(e) => { const btns = [...form.ctaButtons]; btns[idx] = { ...btns[idx], value: e.target.value }; setForm((f) => ({ ...f, ctaButtons: btns })); }} placeholder={btn.type === 'URL' ? 'https://example.com' : '+91XXXXXXXXXX'} className={inputCls()} />
                  </div>
                ))}
              </div>
            </div>
          </div>
        );

      // ── Quick Reply ───────────────────────────────────────────────────────
      case 'twilio/quick-reply':
        return (
          <div className="space-y-3">
            <div>
              <label className="block text-xs font-semibold text-foreground mb-1.5">Body <span className="text-red-500">*</span></label>
              <textarea value={form.qrBody} onChange={(e) => { setForm((f) => ({ ...f, qrBody: e.target.value })); setFormErrors((er) => ({ ...er, qrBody: '' })); }} rows={2} placeholder="Would you like to proceed?" className={`${inputCls(formErrors.qrBody)} resize-none`} />
              {formErrors.qrBody && <p className="text-xs text-red-500 mt-1">{formErrors.qrBody}</p>}
            </div>
            {renderSampleValueInputs(form.qrBody)}
            <div>
              <div className="flex items-center justify-between mb-1.5">
                <label className="text-xs font-semibold text-foreground">Quick Reply Buttons <span className="text-red-500">*</span></label>
                {form.qrButtons.length < 3 && (
                  <button type="button" onClick={() => setForm((f) => ({ ...f, qrButtons: [...f.qrButtons, { id: String(Date.now()), title: '' }] }))} className="text-xs text-primary hover:underline">+ Add button</button>
                )}
              </div>
              {formErrors.qrButtons && <p className="text-xs text-red-500 mb-1">{formErrors.qrButtons}</p>}
              <div className="space-y-2">
                {form.qrButtons.map((btn, idx) => (
                  <div key={btn.id} className="flex gap-2 items-center">
                    <input type="text" value={btn.title} onChange={(e) => { const btns = [...form.qrButtons]; btns[idx] = { ...btns[idx], title: e.target.value }; setForm((f) => ({ ...f, qrButtons: btns })); }} placeholder={`Button ${idx + 1} label`} className={inputCls()} />
                    {form.qrButtons.length > 1 && (
                      <button type="button" onClick={() => setForm((f) => ({ ...f, qrButtons: f.qrButtons.filter((_, i) => i !== idx) }))} className="p-1.5 rounded hover:bg-red-50 text-muted-foreground hover:text-red-500 transition-colors"><X size={13} /></button>
                    )}
                  </div>
                ))}
              </div>
            </div>
          </div>
        );

      // ── Card / WhatsApp Card ──────────────────────────────────────────────
      case 'twilio/card': case'twilio/whatsapp-card':
        return (
          <div className="space-y-3">
            <div>
              <label className="block text-xs font-semibold text-foreground mb-1.5">Card Title <span className="text-red-500">*</span></label>
              <input type="text" value={form.cardTitle} onChange={(e) => { setForm((f) => ({ ...f, cardTitle: e.target.value })); setFormErrors((er) => ({ ...er, cardTitle: '' })); }} placeholder="Card title" className={inputCls(formErrors.cardTitle)} />
              {formErrors.cardTitle && <p className="text-xs text-red-500 mt-1">{formErrors.cardTitle}</p>}
            </div>
            <div>
              <label className="block text-xs font-semibold text-foreground mb-1.5">Card Body <span className="text-red-500">*</span></label>
              <textarea value={form.cardBody} onChange={(e) => { setForm((f) => ({ ...f, cardBody: e.target.value })); setFormErrors((er) => ({ ...er, cardBody: '' })); }} rows={2} placeholder="Card body text..." className={`${inputCls(formErrors.cardBody)} resize-none`} />
              {formErrors.cardBody && <p className="text-xs text-red-500 mt-1">{formErrors.cardBody}</p>}
            </div>
            {renderSampleValueInputs(form.cardBody)}
            <div>
              <label className="block text-xs font-semibold text-foreground mb-1.5">Media URL (optional)</label>
              <input type="url" value={form.cardMedia} onChange={(e) => setForm((f) => ({ ...f, cardMedia: e.target.value }))} placeholder="https://example.com/image.jpg" className={inputCls()} />
            </div>
            <div>
              <div className="flex items-center justify-between mb-1.5">
                <label className="text-xs font-semibold text-foreground">Buttons</label>
                {form.cardButtons.length < 3 && (
                  <button type="button" onClick={() => setForm((f) => ({ ...f, cardButtons: [...f.cardButtons, { type: 'URL', text: '', value: '' }] }))} className="text-xs text-primary hover:underline">+ Add button</button>
                )}
              </div>
              <div className="space-y-2">
                {form.cardButtons.map((btn, idx) => (
                  <div key={idx} className="border border-border rounded-lg p-3 space-y-2">
                    <div className="flex gap-2">
                      <select value={btn.type} onChange={(e) => { const btns = [...form.cardButtons]; btns[idx] = { ...btns[idx], type: e.target.value as CardButton['type'] }; setForm((f) => ({ ...f, cardButtons: btns })); }} className={`${inputCls()} w-40`}>
                        <option value="URL">URL</option>
                        <option value="PHONE_NUMBER">Phone</option>
                        <option value="QUICK_REPLY">Quick Reply</option>
                      </select>
                      {form.cardButtons.length > 1 && (
                        <button type="button" onClick={() => setForm((f) => ({ ...f, cardButtons: f.cardButtons.filter((_, i) => i !== idx) }))} className="p-1.5 rounded hover:bg-red-50 text-muted-foreground hover:text-red-500 transition-colors"><X size={13} /></button>
                      )}
                    </div>
                    <input type="text" value={btn.text} onChange={(e) => { const btns = [...form.cardButtons]; btns[idx] = { ...btns[idx], text: e.target.value }; setForm((f) => ({ ...f, cardButtons: btns })); }} placeholder="Button label" className={inputCls()} />
                    {btn.type !== 'QUICK_REPLY' && (
                      <input type="text" value={btn.value ?? ''} onChange={(e) => { const btns = [...form.cardButtons]; btns[idx] = { ...btns[idx], value: e.target.value }; setForm((f) => ({ ...f, cardButtons: btns })); }} placeholder={btn.type === 'URL' ? 'https://...' : '+91XXXXXXXXXX'} className={inputCls()} />
                    )}
                  </div>
                ))}
              </div>
            </div>
          </div>
        );

      // ── Carousel ──────────────────────────────────────────────────────────
      case 'twilio/carousel':
        return (
          <div className="space-y-3">
            {formErrors.carouselCards && <p className="text-xs text-red-500">{formErrors.carouselCards}</p>}
            {form.carouselCards.map((card, ci) => (
              <div key={ci} className="border border-border rounded-lg p-3 space-y-2">
                <div className="flex items-center justify-between">
                  <span className="text-xs font-semibold text-foreground">Card {ci + 1}</span>
                  {form.carouselCards.length > 1 && (
                    <button type="button" onClick={() => setForm((f) => ({ ...f, carouselCards: f.carouselCards.filter((_, i) => i !== ci) }))} className="p-1 rounded hover:bg-red-50 text-muted-foreground hover:text-red-500 transition-colors"><X size={12} /></button>
                  )}
                </div>
                <input type="text" value={card.title} onChange={(e) => { const cards = [...form.carouselCards]; cards[ci] = { ...cards[ci], title: e.target.value }; setForm((f) => ({ ...f, carouselCards: cards })); }} placeholder="Card title" className={inputCls()} />
                <textarea value={card.body} onChange={(e) => { const cards = [...form.carouselCards]; cards[ci] = { ...cards[ci], body: e.target.value }; setForm((f) => ({ ...f, carouselCards: cards })); }} rows={2} placeholder="Card body..." className={`${inputCls()} resize-none`} />
                <input type="url" value={card.media ?? ''} onChange={(e) => { const cards = [...form.carouselCards]; cards[ci] = { ...cards[ci], media: e.target.value }; setForm((f) => ({ ...f, carouselCards: cards })); }} placeholder="Media URL (optional)" className={inputCls()} />
              </div>
            ))}
            {form.carouselCards.length < 10 && (
              <button type="button" onClick={() => setForm((f) => ({ ...f, carouselCards: [...f.carouselCards, { title: '', body: '', media: '', buttons: [{ type: 'URL', text: '', value: '' }] }] }))} className="w-full py-2 border-2 border-dashed border-border rounded-lg text-xs text-muted-foreground hover:border-primary/50 hover:text-primary transition-colors">+ Add Card</button>
            )}
          </div>
        );

      // ── Authentication ────────────────────────────────────────────────────
      case 'twilio/authentication':
        return (
          <div className="space-y-3">
            <div className="bg-blue-50 border border-blue-200 rounded-lg px-3 py-2.5">
              <p className="text-xs text-blue-700">Authentication templates send a one-time passcode (OTP). The code variable <strong>{'{{1}}'}</strong> is automatically replaced with the generated code.</p>
            </div>
            <div>
              <label className="block text-xs font-semibold text-foreground mb-1.5">Code Expiry (minutes)</label>
              <input type="number" min="1" max="60" value={form.authExpiry} onChange={(e) => setForm((f) => ({ ...f, authExpiry: e.target.value }))} className={inputCls()} />
            </div>
            <div className="bg-muted/50 rounded-lg px-3 py-2.5">
              <p className="text-xs text-muted-foreground font-medium mb-1">Preview message:</p>
              <p className="text-sm text-foreground">Your verification code is <strong>{'{{1}}'}</strong>. Valid for {form.authExpiry} minutes.</p>
            </div>
          </div>
        );

      // ── Catalog ───────────────────────────────────────────────────────────
      case 'twilio/catalog':
        return (
          <div className="space-y-3">
            <div>
              <label className="block text-xs font-semibold text-foreground mb-1.5">Body <span className="text-red-500">*</span></label>
              <textarea value={form.catalogBody} onChange={(e) => { setForm((f) => ({ ...f, catalogBody: e.target.value })); setFormErrors((er) => ({ ...er, catalogBody: '' })); }} rows={2} placeholder="Browse our latest products..." className={`${inputCls(formErrors.catalogBody)} resize-none`} />
              {formErrors.catalogBody && <p className="text-xs text-red-500 mt-1">{formErrors.catalogBody}</p>}
            </div>
            {renderSampleValueInputs(form.catalogBody)}
            <div>
              <label className="block text-xs font-semibold text-foreground mb-1.5">Catalog ID (optional)</label>
              <input type="text" value={form.catalogId} onChange={(e) => setForm((f) => ({ ...f, catalogId: e.target.value }))} placeholder="Your Meta catalog ID" className={inputCls()} />
            </div>
          </div>
        );

      // ── Flows ─────────────────────────────────────────────────────────────
      case 'twilio/flows':
        return (
          <div className="space-y-3">
            <div>
              <label className="block text-xs font-semibold text-foreground mb-1.5">Body <span className="text-red-500">*</span></label>
              <textarea value={form.flowsBody} onChange={(e) => { setForm((f) => ({ ...f, flowsBody: e.target.value })); setFormErrors((er) => ({ ...er, flowsBody: '' })); }} rows={2} placeholder="Start the flow to continue..." className={`${inputCls(formErrors.flowsBody)} resize-none`} />
              {formErrors.flowsBody && <p className="text-xs text-red-500 mt-1">{formErrors.flowsBody}</p>}
            </div>
            {renderSampleValueInputs(form.flowsBody)}
            <div>
              <label className="block text-xs font-semibold text-foreground mb-1.5">Flow SID <span className="text-red-500">*</span></label>
              <input type="text" value={form.flowSid} onChange={(e) => { setForm((f) => ({ ...f, flowSid: e.target.value })); setFormErrors((er) => ({ ...er, flowSid: '' })); }} placeholder="FWxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxx" className={inputCls(formErrors.flowSid)} />
              {formErrors.flowSid && <p className="text-xs text-red-500 mt-1">{formErrors.flowSid}</p>}
            </div>
            <div>
              <label className="block text-xs font-semibold text-foreground mb-1.5">Button Label</label>
              <input type="text" value={form.flowButton} onChange={(e) => setForm((f) => ({ ...f, flowButton: e.target.value }))} placeholder="Open Flow" className={inputCls()} />
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-foreground">Campaign Management</h1>
          <p className="text-sm text-muted-foreground mt-0.5">WhatsApp drip campaigns · {campaignList.filter((c) => c.status === 'Active').length} active</p>
        </div>
        <button
          onClick={handleOpenCreateCampaign}
          className="flex items-center gap-2 px-4 py-2 bg-primary text-white rounded-lg text-sm font-medium hover:bg-primary/90 transition-colors active:scale-95"
        >
          <Plus size={16} />
          New Campaign
        </button>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 border-b border-border">
        {(['campaigns', 'templates', 'trackable-links'] as const).map((tab) => (
          <button
            key={`tab-${tab}`}
            onClick={() => setActiveTab(tab)}
            className={`px-4 py-2.5 text-sm font-semibold capitalize transition-colors border-b-2 -mb-px ${
              activeTab === tab ? 'border-primary text-primary' : 'border-transparent text-muted-foreground hover:text-foreground'
            }`}
          >
            {tab === 'campaigns' ? 'Drip Campaigns' : tab === 'templates' ? 'Template Library' : 'Trackable Links'}
          </button>
        ))}
      </div>

      {activeTab === 'campaigns' && (
        <div className="grid grid-cols-1 lg:grid-cols-5 xl:grid-cols-5 2xl:grid-cols-5 gap-6">
          {/* Campaign List */}
          <div className="lg:col-span-3 space-y-3">
            {loadingCampaigns ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="w-6 h-6 animate-spin text-primary" />
                <span className="ml-2 text-sm text-muted-foreground">Loading campaigns...</span>
              </div>
            ) : campaignList.length === 0 ? (
              <div className="text-center py-12 text-muted-foreground text-sm">No campaigns found in drip sequences.</div>
            ) : null}
            {!loadingCampaigns && campaignList.map((camp) => {
              const sc = statusConfig[camp.status];
              return (
                <div
                  key={camp.id}
                  onClick={() => setActiveCampaignId(camp.id)}
                  className={`bg-card border rounded-xl p-4 cursor-pointer transition-all hover:shadow-md ${activeCampaignId === camp.id ? 'border-primary ring-1 ring-primary/20' : 'border-border'}`}
                >
                  <div className="flex items-start justify-between mb-3">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <span className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-xs font-semibold ${sc.bg} ${sc.text}`}>
                          <span className={`w-1.5 h-1.5 rounded-full ${sc.dot}`} />
                          {camp.status}
                        </span>
                        <span className="text-xs text-muted-foreground">{camp.dripDays}-day drip</span>
                      </div>
                      <h3 className="font-semibold text-foreground">{camp.name}</h3>
                      <p className="text-xs text-muted-foreground mt-0.5">{camp.segment}</p>
                    </div>
                    <div className="flex items-center gap-1 ml-3">
                      {camp.status === 'Active' && (
                        <button title="Pause campaign" onClick={(e) => { e.stopPropagation(); toast.success(`${camp.name} paused`); }} className="p-1.5 rounded-lg hover:bg-amber-50 text-muted-foreground hover:text-amber-600 transition-colors"><Pause size={14} /></button>
                      )}
                      {camp.status === 'Paused' && (
                        <button title="Resume campaign" onClick={(e) => { e.stopPropagation(); toast.success(`${camp.name} resumed`); }} className="p-1.5 rounded-lg hover:bg-green-50 text-muted-foreground hover:text-green-600 transition-colors"><Play size={14} /></button>
                      )}
                      {camp.status === 'Draft' && (
                        <button title="Launch campaign" onClick={(e) => { e.stopPropagation(); toast.success(`${camp.name} launched`); }} className="p-1.5 rounded-lg hover:bg-green-50 text-muted-foreground hover:text-green-600 transition-colors"><Send size={14} /></button>
                      )}
                      <button title="Edit campaign" onClick={(e) => { e.stopPropagation(); handleOpenEdit(camp); }} className="p-1.5 rounded-lg hover:bg-muted text-muted-foreground hover:text-foreground transition-colors"><Edit2 size={14} /></button>
                    </div>
                  </div>
                  <div className="grid grid-cols-4 gap-3">
                    {[
                      { label: 'Enrolled', value: camp.enrolled.toLocaleString('en-IN'), icon: Users },
                      { label: 'Sent', value: camp.sent.toLocaleString('en-IN'), icon: Send },
                      { label: 'Delivery', value: camp.deliveryRate, icon: CheckCircle },
                      { label: 'Open Rate', value: camp.openRate, icon: Eye },
                    ].map((stat) => {
                      const StatIcon = stat.icon;
                      return (
                        <div key={`stat-${camp.id}-${stat.label}`} className="text-center">
                          <p className="text-xs text-muted-foreground">{stat.label}</p>
                          <p className="text-sm font-bold tabular-nums text-foreground mt-0.5">{stat.value}</p>
                        </div>
                      );
                    })}
                  </div>
                </div>
              );
            })}
          </div>

          {/* Drip Sequence Viewer */}
          <div className="lg:col-span-2 space-y-4">
            {selectedCampaign && (
              <div className="bg-card border border-border rounded-xl p-5">
                <div className="flex items-center gap-2 mb-4">
                  <Zap size={16} className="text-accent" />
                  <h3 className="text-base font-semibold text-foreground">Drip Sequence</h3>
                  <span className="ml-auto text-xs text-muted-foreground">{selectedCampaign.name}</span>
                  <button
                    title="Edit drip sequence"
                    onClick={() => handleOpenDripEditor(selectedCampaign.id)}
                    className="p-1.5 rounded-lg hover:bg-muted text-muted-foreground hover:text-foreground transition-colors"
                  >
                    <Edit2 size={14} />
                  </button>
                </div>
                {drip.length > 0 ? (
                  <div className="relative">
                    <div className="absolute left-4 top-4 bottom-4 w-0.5 bg-border" />
                    <div className="space-y-4">
                      {drip.map((step, idx) => (
                        <div key={`drip-${activeCampaignId}-${idx}`} className="flex items-start gap-3 relative">
                          <div className="w-8 h-8 rounded-full bg-primary text-white flex items-center justify-center text-xs font-bold shrink-0 z-10">D{step.day}</div>
                          <div className="flex-1 bg-muted/50 rounded-lg p-3 border border-border/50">
                            <p className="text-xs font-semibold text-primary mb-0.5">{step.template}</p>
                            <p className="text-sm text-foreground">{step.description}</p>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                ) : (
                  <div className="text-center py-8 text-muted-foreground">
                    <MessageSquare size={32} className="mx-auto mb-2 opacity-30" />
                    <p className="text-sm">No drip sequence defined yet</p>
                    <button onClick={() => handleOpenDripEditor(selectedCampaign.id)} className="mt-3 text-xs font-medium text-primary hover:underline">Add message steps</button>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      )}

      {activeTab === 'templates' && (
        <>
          {loadingTemplates ? (
            <div className="flex items-center justify-center py-16"><Loader2 size={28} className="animate-spin text-primary" /></div>
          ) : (
            <>
              {/* Template Library Header */}
              <div className="flex items-center justify-between mb-4">
                <div>
                  <p className="text-sm font-semibold text-foreground">
                    Template Library
                    {templates.length > 0 && (
                      <span className="ml-2 text-xs font-normal text-muted-foreground">
                        {templates.filter((t) => t.status === 'Approved').length} approved ·{' '}
                        {templates.filter((t) => !t.content_sid).length} pending submission
                      </span>
                    )}
                  </p>
                </div>
                <button
                  onClick={handleSubmitAllForApproval}
                  disabled={submittingAll || templates.filter((t) => !t.content_sid).length === 0}
                  title="Submit all pending templates to Twilio/Meta for WhatsApp approval"
                  className="flex items-center gap-2 px-3 py-2 text-sm font-semibold rounded-lg bg-primary text-white hover:bg-primary/90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed active:scale-95"
                >
                  {submittingAll ? (
                    <Loader2 size={14} className="animate-spin" />
                  ) : (
                    <Upload size={14} />
                  )}
                  {submittingAll ? 'Submitting All…' : 'Submit All for Approval'}
                </button>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 2xl:grid-cols-4 gap-4">
              {templates.map((tpl) => {
                const isSubmitting = submittingId === tpl.id;
                const isSyncing = syncingId === tpl.id;
                const canSubmit = !tpl.content_sid && tpl.status !== 'Approved';
                const canSync = !!tpl.content_sid && tpl.status !== 'Approved' && tpl.status !== 'Rejected';
                return (
                  <div key={tpl.id} className="bg-card border border-border rounded-xl p-4 hover:shadow-md transition-shadow flex flex-col">
                    <div className="flex items-start justify-between mb-3">
                      <div className="w-9 h-9 rounded-lg bg-primary/10 flex items-center justify-center shrink-0">
                        <FileText size={16} className="text-primary" />
                      </div>
                      <div className="flex flex-col items-end gap-1">
                        <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${tpl.status === 'Approved' ? 'bg-green-50 text-green-700' : tpl.status === 'Rejected' ? 'bg-red-50 text-red-700' : tpl.status === 'Paused' || tpl.status === 'Disabled' ? 'bg-gray-100 text-gray-600' : 'bg-amber-50 text-amber-700'}`}>
                          {tpl.status}
                        </span>
                        {tpl.content_sid && (
                          <span className="text-[10px] font-mono text-muted-foreground truncate max-w-[100px]" title={tpl.content_sid}>{tpl.content_sid.slice(0, 10)}…</span>
                        )}
                      </div>
                    </div>
                    <h4 className="font-semibold text-foreground text-sm mb-1">{tpl.label}</h4>
                    <p className="text-xs text-muted-foreground mb-1 font-mono">{tpl.name}</p>
                    <p className="text-xs text-muted-foreground mb-3 line-clamp-2 flex-1">{tpl.body}</p>
                    <div className="flex items-center gap-1.5 mb-2">
                      {canSubmit && (
                        <button onClick={() => handleSubmitForApproval(tpl)} disabled={isSubmitting || !!submittingId} title="Submit to Twilio for Meta/WhatsApp approval" className="flex items-center gap-1 px-2 py-1 text-xs font-medium rounded-lg bg-primary/10 text-primary hover:bg-primary/20 transition-colors disabled:opacity-50 disabled:cursor-not-allowed">
                          {isSubmitting ? <Loader2 size={11} className="animate-spin" /> : <Upload size={11} />}
                          {isSubmitting ? 'Submitting…' : 'Submit for Approval'}
                        </button>
                      )}
                      {canSync && (
                        <button onClick={() => handleSyncStatus(tpl)} disabled={isSyncing || !!syncingId} title="Sync approval status from Twilio" className="flex items-center gap-1 px-2 py-1 text-xs font-medium rounded-lg bg-muted text-muted-foreground hover:bg-muted/80 hover:text-foreground transition-colors disabled:opacity-50 disabled:cursor-not-allowed">
                          <RefreshCw size={11} className={isSyncing ? 'animate-spin' : ''} />
                          {isSyncing ? 'Syncing…' : 'Sync Status'}
                        </button>
                      )}
                      {tpl.status === 'Approved' && (
                        <span className="flex items-center gap-1 text-xs text-green-600 font-medium"><CheckCircle size={11} />Ready to use</span>
                      )}
                    </div>
                    <div className="flex items-center justify-between border-t border-border/50 pt-2 mt-auto">
                      <span className="text-xs text-muted-foreground">{tpl.uses} uses</span>
                      <div className="flex gap-1">
                        <button title="Preview template" onClick={() => setPreviewTpl(tpl)} className="p-1.5 rounded-lg hover:bg-muted text-muted-foreground hover:text-foreground transition-colors"><Eye size={13} /></button>
                        <button title="Edit template" onClick={() => handleOpenEditTemplate(tpl)} className="p-1.5 rounded-lg hover:bg-muted text-muted-foreground hover:text-foreground transition-colors"><Edit2 size={13} /></button>
                        {tpl.status === 'Rejected' && (
                          <button title="Delete rejected template" onClick={() => setDeletingTpl(tpl)} className="p-1.5 rounded-lg hover:bg-red-50 text-muted-foreground hover:text-red-500 transition-colors"><Trash2 size={13} /></button>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}

              {/* Create Template Card */}
              <button
                onClick={() => setShowModal(true)}
                className="bg-muted/30 border-2 border-dashed border-border rounded-xl p-4 flex flex-col items-center justify-center gap-2 hover:border-primary/50 hover:bg-primary/5 transition-colors group cursor-pointer min-h-[160px]"
              >
                <Plus size={20} className="text-muted-foreground group-hover:text-primary transition-colors" />
                <span className="text-sm font-medium text-muted-foreground group-hover:text-primary transition-colors">Create Template</span>
              </button>
            </div>
            </>
          )}
        </>
      )}

      {activeTab === 'trackable-links' && (
        <TrackableLinkGenerator campaigns={campaignList.map((c) => ({ id: c.id, name: c.name }))} />
      )}

      {/* ── Delete Confirmation Modal ── */}
      {deletingTpl && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
          <div className="bg-card border border-border rounded-2xl shadow-2xl w-full max-w-sm">
            <div className="flex items-center justify-between px-5 py-4 border-b border-border">
              <h2 className="text-sm font-semibold text-foreground">Delete Rejected Template</h2>
              <button onClick={() => setDeletingTpl(null)} disabled={confirmingDelete} className="p-1.5 rounded-lg hover:bg-muted text-muted-foreground hover:text-foreground transition-colors"><X size={15} /></button>
            </div>
            <div className="px-5 py-4 space-y-3">
              <div className="flex items-start gap-3 p-3 bg-red-50 border border-red-200 rounded-lg">
                <Trash2 size={16} className="text-red-500 mt-0.5 shrink-0" />
                <p className="text-sm text-red-700">
                  Are you sure you want to permanently delete <strong>&ldquo;{deletingTpl.label}&rdquo;</strong>? This action cannot be undone.
                </p>
              </div>
              <p className="text-xs text-muted-foreground">Template key: <span className="font-mono">{deletingTpl.name}</span></p>
            </div>
            <div className="flex items-center justify-end gap-3 px-5 py-4 border-t border-border">
              <button onClick={() => setDeletingTpl(null)} disabled={confirmingDelete} className="px-4 py-2 text-sm font-medium text-muted-foreground hover:text-foreground border border-border rounded-lg hover:bg-muted transition-colors disabled:opacity-50">
                Cancel
              </button>
              <button onClick={handleDeleteRejectedTemplate} disabled={confirmingDelete} className="flex items-center gap-2 px-4 py-2 bg-red-600 text-white text-sm font-medium rounded-lg hover:bg-red-700 transition-colors disabled:opacity-60 active:scale-95">
                {confirmingDelete ? <><Loader2 size={14} className="animate-spin" />Deleting…</> : <><Trash2 size={14} />Delete Template</>}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Create Template Modal ── */}
      {showModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
          <div className="bg-card border border-border rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] flex flex-col">
            {/* Modal Header */}
            <div className="flex items-center justify-between px-6 py-4 border-b border-border shrink-0">
              <div className="flex items-center gap-2">
                <FileText size={18} className="text-primary" />
                <h2 className="text-base font-semibold text-foreground">{editingTemplateId ? 'Edit WhatsApp Template' : 'New WhatsApp Template'}</h2>
                {modalStep === 2 && selectedTypeInfo && (
                  <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${selectedTypeInfo.color}`}>{selectedTypeInfo.label}</span>
                )}
              </div>
              <div className="flex items-center gap-3">
                {/* Step indicator */}
                <div className="flex items-center gap-1.5">
                  <div className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold ${modalStep >= 1 ? 'bg-primary text-white' : 'bg-muted text-muted-foreground'}`}>1</div>
                  <div className={`w-8 h-0.5 ${modalStep >= 2 ? 'bg-primary' : 'bg-border'}`} />
                  <div className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold ${modalStep >= 2 ? 'bg-primary text-white' : 'bg-muted text-muted-foreground'}`}>2</div>
                </div>
                <button onClick={handleCloseModal} className="p-1.5 rounded-lg hover:bg-muted text-muted-foreground hover:text-foreground transition-colors"><X size={16} /></button>
              </div>
            </div>

            {/* Modal Body */}
            <div className="px-6 py-5 overflow-y-auto flex-1">
              {/* ── Step 1: Content Type Picker ── */}
              {modalStep === 1 && (
                <div>
                  <p className="text-sm font-semibold text-foreground mb-1">Content Type</p>
                  <p className="text-xs text-muted-foreground mb-4">Select one of the message types below</p>
                  <div className="grid grid-cols-2 gap-2.5">
                    {CONTENT_TYPES.map((ct) => {
                      const Icon = ct.icon;
                      const isSelected = form.contentType === ct.key;
                      return (
                        <button
                          key={ct.key}
                          type="button"
                          onClick={() => setForm((f) => ({ ...f, contentType: ct.key }))}
                          className={`flex items-center gap-3 px-4 py-3 rounded-xl border-2 text-left transition-all hover:shadow-sm ${isSelected ? 'border-primary bg-primary/5' : 'border-border hover:border-primary/40 bg-background'}`}
                        >
                          <div className={`w-8 h-8 rounded-lg flex items-center justify-center shrink-0 ${ct.color}`}>
                            <Icon size={16} />
                          </div>
                          <div className="min-w-0">
                            <p className={`text-sm font-semibold truncate ${isSelected ? 'text-primary' : 'text-foreground'}`}>{ct.label}</p>
                            <p className="text-xs text-muted-foreground line-clamp-1">{ct.description}</p>
                          </div>
                          <div className={`ml-auto w-4 h-4 rounded-full border-2 shrink-0 flex items-center justify-center ${isSelected ? 'border-primary' : 'border-border'}`}>
                            {isSelected && <div className="w-2 h-2 rounded-full bg-primary" />}
                          </div>
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* ── Step 2: Template Details ── */}
              {modalStep === 2 && (
                <div className="space-y-4">
                  {/* Label */}
                  <div>
                    <label className="block text-xs font-semibold text-foreground mb-1.5">Template Label <span className="text-red-500">*</span></label>
                    <input type="text" value={form.label} onChange={(e) => handleLabelChange(e.target.value)} placeholder="e.g. Welcome Message" className={inputCls(formErrors.label)} />
                    {formErrors.label && <p className="text-xs text-red-500 mt-1">{formErrors.label}</p>}
                  </div>

                  {/* Template Key */}
                  <div>
                    <label className="block text-xs font-semibold text-foreground mb-1.5">Template Key <span className="text-xs font-normal text-muted-foreground">(auto-generated, editable)</span></label>
                    <input type="text" value={form.name} onChange={(e) => { setForm((f) => ({ ...f, name: slugify(e.target.value) })); setFormErrors((er) => ({ ...er, name: '' })); }} placeholder="e.g. welcome_message" className={`${inputCls(formErrors.name)} font-mono`} />
                    {formErrors.name && <p className="text-xs text-red-500 mt-1">{formErrors.name}</p>}
                  </div>

                  {/* Category + Language */}
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="block text-xs font-semibold text-foreground mb-1.5">Category</label>
                      <select value={form.category} onChange={(e) => setForm((f) => ({ ...f, category: e.target.value }))} className={inputCls()}>
                        {CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
                      </select>
                    </div>
                    <div>
                      <label className="block text-xs font-semibold text-foreground mb-1.5">Language</label>
                      <select value={form.language} onChange={(e) => setForm((f) => ({ ...f, language: e.target.value }))} className={inputCls()}>
                        {LANGUAGES.map((l) => <option key={l.code} value={l.code}>{l.label}</option>)}
                      </select>
                    </div>
                  </div>

                  {/* Type-specific fields */}
                  {renderTypeFields()}

                  {/* Info note */}
                  <div className="bg-blue-50 border border-blue-200 rounded-lg px-3 py-2.5">
                    <p className="text-xs text-blue-700">
                      <strong>Tip:</strong> After saving, click <em>Submit for Approval</em> on the template card to send it to Twilio → Meta for WhatsApp review.
                    </p>
                  </div>
                </div>
              )}
            </div>

            {/* Modal Footer */}
            <div className="flex items-center justify-between px-6 py-4 border-t border-border shrink-0">
              <div>
                {modalStep === 2 && (
                  <button onClick={() => { setModalStep(1); setFormErrors({}); }} className="flex items-center gap-1.5 px-3 py-2 text-sm font-medium text-muted-foreground hover:text-foreground border border-border rounded-lg hover:bg-muted transition-colors">
                    <ChevronLeft size={14} />
                    Back
                  </button>
                )}
              </div>
              <div className="flex items-center gap-3">
                <button onClick={handleCloseModal} disabled={saving} className="px-4 py-2 text-sm font-medium text-muted-foreground hover:text-foreground border border-border rounded-lg hover:bg-muted transition-colors disabled:opacity-50">
                  Cancel
                </button>
                {modalStep === 1 ? (
                  <button onClick={() => setModalStep(2)} className="flex items-center gap-2 px-4 py-2 bg-primary text-white text-sm font-medium rounded-lg hover:bg-primary/90 transition-colors active:scale-95">
                    Next
                    <ChevronRight size={14} />
                  </button>
                ) : (
                  <button onClick={handleSave} disabled={saving} className="flex items-center gap-2 px-4 py-2 bg-primary text-white text-sm font-medium rounded-lg hover:bg-primary/90 transition-colors disabled:opacity-60 active:scale-95">
                    {saving ? <><Loader2 size={14} className="animate-spin" />Saving…</> : <><Save size={14} />{editingTemplateId ? 'Update Template' : 'Save Template'}</>}
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ── Preview Modal ── */}
      {previewTpl && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
          <div className="bg-card border border-border rounded-2xl shadow-2xl w-full max-w-sm">
            <div className="flex items-center justify-between px-5 py-4 border-b border-border">
              <h2 className="text-sm font-semibold text-foreground">{previewTpl.label}</h2>
              <button onClick={() => setPreviewTpl(null)} className="p-1.5 rounded-lg hover:bg-muted text-muted-foreground hover:text-foreground transition-colors"><X size={15} /></button>
            </div>
            <div className="px-5 py-4 space-y-3">
              <div className="flex items-center gap-2">
                <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${previewTpl.status === 'Approved' ? 'bg-green-50 text-green-700' : 'bg-amber-50 text-amber-700'}`}>{previewTpl.status}</span>
                <span className="text-xs text-muted-foreground">{previewTpl.category}</span>
                <span className="text-xs text-muted-foreground">· {previewTpl.language}</span>
              </div>
              <p className="text-xs font-mono text-muted-foreground">{previewTpl.name}</p>
              <div className="bg-[#dcf8c6] rounded-xl rounded-tl-none px-4 py-3 text-sm text-gray-800 leading-relaxed shadow-sm">{previewTpl.body}</div>
              <p className="text-xs text-muted-foreground">{previewTpl.uses} total uses</p>
            </div>
          </div>
        </div>
      )}

      {/* ── Create Campaign Modal ── */}
      {showCreateCampaign && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
          <div className="bg-card border border-border rounded-2xl shadow-2xl w-full max-w-lg">
            {/* Header */}
            <div className="flex items-center justify-between px-6 py-4 border-b border-border">
              <div className="flex items-center gap-2">
                <Plus size={16} className="text-primary" />
                <h2 className="text-base font-semibold text-foreground">New Campaign</h2>
              </div>
              <button onClick={handleCloseCreateCampaign} className="p-1.5 rounded-lg hover:bg-muted text-muted-foreground hover:text-foreground transition-colors"><X size={16} /></button>
            </div>

            {/* Body */}
            <div className="px-6 py-5 space-y-4">
              {/* Campaign Name */}
              <div>
                <label className="block text-xs font-semibold text-foreground mb-1.5">Campaign Name <span className="text-red-500">*</span></label>
                <input
                  type="text"
                  value={createForm.name ?? ''}
                  onChange={(e) => { setCreateForm((f) => ({ ...f, name: e.target.value })); setCreateErrors((er) => ({ ...er, name: '' })); }}
                  placeholder="e.g. NEET 600+ India Nurture"
                  className={`w-full px-3 py-2 text-sm border rounded-lg bg-background text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/30 transition-colors ${createErrors.name ? 'border-red-400' : 'border-border'}`}
                />
                {createErrors.name && <p className="text-xs text-red-500 mt-1">{createErrors.name}</p>}
              </div>

              {/* Status */}
              <div>
                <label className="block text-xs font-semibold text-foreground mb-1.5">Status</label>
                <select
                  value={createForm.status ?? 'Draft'}
                  onChange={(e) => setCreateForm((f) => ({ ...f, status: e.target.value as CampaignStatus }))}
                  className="w-full px-3 py-2 text-sm border border-border rounded-lg bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary/30 transition-colors"
                >
                  <option value="Active">Active</option>
                  <option value="Paused">Paused</option>
                  <option value="Draft">Draft</option>
                  <option value="Completed">Completed</option>
                </select>
              </div>

              {/* Segment */}
              <div>
                <label className="block text-xs font-semibold text-foreground mb-1.5">Segment / Audience <span className="text-red-500">*</span></label>
                <input
                  type="text"
                  value={createForm.segment ?? ''}
                  onChange={(e) => { setCreateForm((f) => ({ ...f, segment: e.target.value })); setCreateErrors((er) => ({ ...er, segment: '' })); }}
                  placeholder="e.g. NEET 600+, India"
                  className={`w-full px-3 py-2 text-sm border rounded-lg bg-background text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/30 transition-colors ${createErrors.segment ? 'border-red-400' : 'border-border'}`}
                />
                {createErrors.segment && <p className="text-xs text-red-500 mt-1">{createErrors.segment}</p>}
              </div>

              {/* Drip Days + Start Date */}
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-semibold text-foreground mb-1.5">Drip Days <span className="text-red-500">*</span></label>
                  <input
                    type="number"
                    min={1}
                    max={30}
                    value={createForm.dripDays ?? 7}
                    onChange={(e) => { setCreateForm((f) => ({ ...f, dripDays: parseInt(e.target.value) || 1 })); setCreateErrors((er) => ({ ...er, dripDays: '' })); }}
                    className={`w-full px-3 py-2 text-sm border rounded-lg bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary/30 transition-colors ${createErrors.dripDays ? 'border-red-400' : 'border-border'}`}
                  />
                  {createErrors.dripDays && <p className="text-xs text-red-500 mt-1">{createErrors.dripDays}</p>}
                </div>
                <div>
                  <label className="block text-xs font-semibold text-foreground mb-1.5">Start Date</label>
                  <input
                    type="text"
                    value={createForm.startDate ?? ''}
                    onChange={(e) => setCreateForm((f) => ({ ...f, startDate: e.target.value }))}
                    placeholder="e.g. 01 Jul 2026"
                    className="w-full px-3 py-2 text-sm border border-border rounded-lg bg-background text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/30 transition-colors"
                  />
                </div>
              </div>
            </div>

            {/* Footer */}
            <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-border">
              <button
                onClick={handleCloseCreateCampaign}
                disabled={createSaving}
                className="px-4 py-2 text-sm font-medium text-muted-foreground hover:text-foreground border border-border rounded-lg hover:bg-muted transition-colors disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                onClick={handleCreateSave}
                disabled={createSaving}
                className="flex items-center gap-2 px-4 py-2 bg-primary text-white text-sm font-medium rounded-lg hover:bg-primary/90 transition-colors disabled:opacity-60 active:scale-95"
              >
                {createSaving ? <><Loader2 size={14} className="animate-spin" />Creating…</> : <><Plus size={14} />Create Campaign</>}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Edit Campaign Modal ── */}
      {editingCampaign && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
          <div className="bg-card border border-border rounded-2xl shadow-2xl w-full max-w-lg">
            {/* Header */}
            <div className="flex items-center justify-between px-6 py-4 border-b border-border">
              <div className="flex items-center gap-2">
                <Edit2 size={16} className="text-primary" />
                <h2 className="text-base font-semibold text-foreground">Edit Campaign</h2>
              </div>
              <button onClick={handleCloseEdit} className="p-1.5 rounded-lg hover:bg-muted text-muted-foreground hover:text-foreground transition-colors"><X size={16} /></button>
            </div>

            {/* Body */}
            <div className="px-6 py-5 space-y-4">
              {/* Campaign Name */}
              <div>
                <label className="block text-xs font-semibold text-foreground mb-1.5">Campaign Name <span className="text-red-500">*</span></label>
                <input
                  type="text"
                  value={editForm.name ?? ''}
                  onChange={(e) => { setEditForm((f) => ({ ...f, name: e.target.value })); setEditErrors((er) => ({ ...er, name: '' })); }}
                  placeholder="e.g. NEET 600+ India Nurture"
                  className={`w-full px-3 py-2 text-sm border rounded-lg bg-background text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/30 transition-colors ${editErrors.name ? 'border-red-400' : 'border-border'}`}
                />
                {editErrors.name && <p className="text-xs text-red-500 mt-1">{editErrors.name}</p>}
              </div>

              {/* Status */}
              <div>
                <label className="block text-xs font-semibold text-foreground mb-1.5">Status</label>
                <select
                  value={editForm.status ?? 'Draft'}
                  onChange={(e) => setEditForm((f) => ({ ...f, status: e.target.value as CampaignStatus }))}
                  className="w-full px-3 py-2 text-sm border border-border rounded-lg bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary/30 transition-colors"
                >
                  <option value="Active">Active</option>
                  <option value="Paused">Paused</option>
                  <option value="Draft">Draft</option>
                  <option value="Completed">Completed</option>
                </select>
              </div>

              {/* Segment */}
              <div>
                <label className="block text-xs font-semibold text-foreground mb-1.5">Segment / Audience <span className="text-red-500">*</span></label>
                <input
                  type="text"
                  value={editForm.segment ?? ''}
                  onChange={(e) => { setEditForm((f) => ({ ...f, segment: e.target.value })); setEditErrors((er) => ({ ...er, segment: '' })); }}
                  placeholder="e.g. NEET 600+, India"
                  className={`w-full px-3 py-2 text-sm border rounded-lg bg-background text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/30 transition-colors ${editErrors.segment ? 'border-red-400' : 'border-border'}`}
                />
                {editErrors.segment && <p className="text-xs text-red-500 mt-1">{editErrors.segment}</p>}
              </div>

              {/* Drip Days + Start Date */}
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-semibold text-foreground mb-1.5">Drip Days <span className="text-red-500">*</span></label>
                  <input
                    type="number"
                    min={1}
                    max={30}
                    value={editForm.dripDays ?? 1}
                    onChange={(e) => { setEditForm((f) => ({ ...f, dripDays: parseInt(e.target.value) || 1 })); setEditErrors((er) => ({ ...er, dripDays: '' })); }}
                    className={`w-full px-3 py-2 text-sm border rounded-lg bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary/30 transition-colors ${editErrors.dripDays ? 'border-red-400' : 'border-border'}`}
                  />
                  {editErrors.dripDays && <p className="text-xs text-red-500 mt-1">{editErrors.dripDays}</p>}
                </div>
                <div>
                  <label className="block text-xs font-semibold text-foreground mb-1.5">Start Date</label>
                  <input
                    type="text"
                    value={editForm.startDate ?? ''}
                    onChange={(e) => setEditForm((f) => ({ ...f, startDate: e.target.value }))}
                    placeholder="e.g. 01 May 2026"
                    className="w-full px-3 py-2 text-sm border border-border rounded-lg bg-background text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/30 transition-colors"
                  />
                </div>
              </div>
            </div>

            {/* Footer */}
            <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-border">
              <button
                onClick={handleCloseEdit}
                disabled={editSaving}
                className="px-4 py-2 text-sm font-medium text-muted-foreground hover:text-foreground border border-border rounded-lg hover:bg-muted transition-colors disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                onClick={handleEditSave}
                disabled={editSaving}
                className="flex items-center gap-2 px-4 py-2 bg-primary text-white text-sm font-medium rounded-lg hover:bg-primary/90 transition-colors disabled:opacity-60 active:scale-95"
              >
                {editSaving ? <><Loader2 size={14} className="animate-spin" />Saving…</> : <><Save size={14} />Save Changes</>}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Drip Sequence Editor Modal ── */}
      {editingDripCampaignId && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
          <div className="bg-card border border-border rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] flex flex-col">
            {/* Header */}
            <div className="flex items-center justify-between px-6 py-4 border-b border-border shrink-0">
              <div className="flex items-center gap-2">
                <Zap size={16} className="text-primary" />
                <h2 className="text-base font-semibold text-foreground">Edit Drip Sequence</h2>
                <span className="text-xs text-muted-foreground">
                  {campaignList.find((c) => c.id === editingDripCampaignId)?.name}
                </span>
              </div>
              <button onClick={handleCloseDripEditor} className="p-1.5 rounded-lg hover:bg-muted text-muted-foreground hover:text-foreground transition-colors">
                <X size={16} />
              </button>
            </div>

            {/* Body */}
            <div className="flex flex-col md:flex-row flex-1 min-h-0 overflow-hidden">
              {/* Step List */}
              <div className="md:w-1/2 border-b md:border-b-0 md:border-r border-border flex flex-col overflow-hidden">
                <div className="flex items-center justify-between px-4 py-3 border-b border-border shrink-0">
                  <span className="text-xs font-semibold text-foreground">Steps ({dripEditSteps.length})</span>
                  <button
                    onClick={handleAddNewStep}
                    className="flex items-center gap-1 text-xs font-medium text-primary hover:underline"
                  >
                    <Plus size={12} />
                    Add Step
                  </button>
                </div>
                <div className="overflow-y-auto flex-1 p-3 space-y-2">
                  {dripEditSteps.length === 0 && (
                    <div className="text-center py-8 text-muted-foreground">
                      <MessageSquare size={28} className="mx-auto mb-2 opacity-30" />
                      <p className="text-xs">No steps yet. Click &quot;Add Step&quot; to begin.</p>
                    </div>
                  )}
                  {dripEditSteps.map((step, idx) => (
                    <div
                      key={`edit-step-${idx}`}
                      className={`flex items-start gap-2 p-3 rounded-lg border transition-colors cursor-pointer ${editingStepIdx === idx ? 'border-primary bg-primary/5' : 'border-border bg-muted/30 hover:bg-muted/60'}`}
                      onClick={() => handleEditStep(idx)}
                    >
                      <GripVertical size={14} className="text-muted-foreground mt-0.5 shrink-0" />
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-1.5 mb-0.5">
                          <span className="w-6 h-6 rounded-full bg-primary text-white flex items-center justify-center text-[10px] font-bold shrink-0">D{step.day}</span>
                          <span className="text-xs font-semibold text-primary truncate">{step.template || '(no template)'}</span>
                        </div>
                        <p className="text-xs text-muted-foreground truncate">{step.description || '(no description)'}</p>
                      </div>
                      <div className="flex flex-col gap-0.5 shrink-0">
                        <button
                          title="Move up"
                          onClick={(e) => { e.stopPropagation(); handleMoveStep(idx, 'up'); }}
                          disabled={idx === 0}
                          className="p-1 rounded hover:bg-muted text-muted-foreground hover:text-foreground disabled:opacity-30 transition-colors"
                        >
                          <ArrowUp size={11} />
                        </button>
                        <button
                          title="Move down"
                          onClick={(e) => { e.stopPropagation(); handleMoveStep(idx, 'down'); }}
                          disabled={idx === dripEditSteps.length - 1}
                          className="p-1 rounded hover:bg-muted text-muted-foreground hover:text-foreground disabled:opacity-30 transition-colors"
                        >
                          <ArrowDown size={11} />
                        </button>
                        <button
                          title="Delete step"
                          onClick={(e) => { e.stopPropagation(); handleDeleteStep(idx); }}
                          className="p-1 rounded hover:bg-red-50 text-muted-foreground hover:text-red-500 transition-colors"
                        >
                          <Trash2 size={11} />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Step Editor */}
              <div className="md:w-1/2 flex flex-col overflow-hidden">
                {editingStepIdx !== null ? (
                  <>
                    <div className="px-4 py-3 border-b border-border shrink-0">
                      <span className="text-xs font-semibold text-foreground">
                        {editingStepIdx === -1 ? 'New Step' : `Edit Step ${editingStepIdx + 1}`}
                      </span>
                    </div>
                    <div className="overflow-y-auto flex-1 p-4 space-y-4">
                      {/* Day */}
                      <div>
                        <label className="block text-xs font-semibold text-foreground mb-1.5">Day <span className="text-red-500">*</span></label>
                        <input
                          type="number"
                          min={0}
                          value={stepForm.day}
                          onChange={(e) => { setStepForm((f) => ({ ...f, day: parseInt(e.target.value) || 0 })); setStepErrors((er) => ({ ...er, day: '' })); }}
                          className={`w-full px-3 py-2 text-sm border rounded-lg bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary/30 transition-colors ${stepErrors.day ? 'border-red-400' : 'border-border'}`}
                        />
                        {stepErrors.day && <p className="text-xs text-red-500 mt-1">{stepErrors.day}</p>}
                        <p className="text-xs text-muted-foreground mt-1">Day 0 = immediately on enrollment</p>
                      </div>

                      {/* Template Key */}
                      <div>
                        <label className="block text-xs font-semibold text-foreground mb-1.5">Template Key <span className="text-red-500">*</span></label>
                        <input
                          type="text"
                          value={stepForm.template}
                          onChange={(e) => { setStepForm((f) => ({ ...f, template: e.target.value })); setStepErrors((er) => ({ ...er, template: '' })); }}
                          placeholder="e.g. welcome_message"
                          className={`w-full px-3 py-2 text-sm border rounded-lg bg-background text-foreground font-mono placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/30 transition-colors ${stepErrors.template ? 'border-red-400' : 'border-border'}`}
                        />
                        {stepErrors.template && <p className="text-xs text-red-500 mt-1">{stepErrors.template}</p>}
                      </div>

                      {/* Description */}
                      <div>
                        <label className="block text-xs font-semibold text-foreground mb-1.5">Description <span className="text-red-500">*</span></label>
                        <textarea
                          value={stepForm.description}
                          onChange={(e) => { setStepForm((f) => ({ ...f, description: e.target.value })); setStepErrors((er) => ({ ...er, description: '' })); }}
                          rows={3}
                          placeholder="Brief description of this message step"
                          className={`w-full px-3 py-2 text-sm border rounded-lg bg-background text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/30 transition-colors resize-none ${stepErrors.description ? 'border-red-400' : 'border-border'}`}
                        />
                        {stepErrors.description && <p className="text-xs text-red-500 mt-1">{stepErrors.description}</p>}
                      </div>
                    </div>
                    <div className="flex items-center justify-end gap-2 px-4 py-3 border-t border-border shrink-0">
                      <button
                        onClick={() => { setEditingStepIdx(null); setStepErrors({}); }}
                        className="px-3 py-1.5 text-xs font-medium text-muted-foreground hover:text-foreground border border-border rounded-lg hover:bg-muted transition-colors"
                      >
                        Cancel
                      </button>
                      <button
                        onClick={handleSaveStep}
                        className="flex items-center gap-1.5 px-3 py-1.5 bg-primary text-white text-xs font-medium rounded-lg hover:bg-primary/90 transition-colors active:scale-95"
                      >
                        <Save size={12} />
                        {editingStepIdx === -1 ? 'Add Step' : 'Update Step'}
                      </button>
                    </div>
                  </>
                ) : (
                  <div className="flex-1 flex flex-col items-center justify-center text-muted-foreground p-6 text-center">
                    <Edit2 size={28} className="mb-2 opacity-30" />
                    <p className="text-sm font-medium">Select a step to edit</p>
                    <p className="text-xs mt-1">or click &quot;Add Step&quot; to create a new one</p>
                  </div>
                )}
              </div>
            </div>

            {/* Footer */}
            <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-border shrink-0">
              <button
                onClick={handleCloseDripEditor}
                className="px-4 py-2 text-sm font-medium text-muted-foreground hover:text-foreground border border-border rounded-lg hover:bg-muted transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleSaveDripSequence}
                disabled={savingDrip}
                className="flex items-center gap-2 px-4 py-2 bg-primary text-white text-sm font-medium rounded-lg hover:bg-primary/90 transition-colors active:scale-95 disabled:opacity-60"
              >
                {savingDrip ? <><Loader2 size={14} className="animate-spin" />Saving…</> : <><Save size={14} />Save Sequence</>}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}